'use client';

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { nanoid } from 'nanoid';
import { graphql, useFragment } from 'react-relay';
import { usePathname, useSearchParams } from 'next/navigation';
import { useTranslation, lang, hocT } from '@/helpers/translate';
import { useZusUserConnectedStore } from '@/helpers/user-auth.js';
import cookie from '@/helpers/cookie.js';
import LoginSignupContainer from '@/components/header/loginsignup.js';
import { useWasUrl } from '@/helpers/router';
import GotoAddSpace from './gotoAddSpace.js';
import { TrackerComponent } from '@/helpers/tracker.js';
import miniqlv3 from '@/helpers/miniQueryLoaderv3';
import { useZusModalStore } from '@/helpers/modal.js';
import useCss1024 from './helpers/useCss1024';
import { useHeaderState } from './hooks/useHeaderState';
import { useHeaderScroll } from './hooks/useHeaderScroll';
import HeaderDesktop from './headDesktop.js';
import HeaderMobile from './headMobile.js';
import CorpoUserMessage from '@/components/corpo/userMessage.js';
import { useDeviceDetect } from '@/helpers/useDeviceDetect';
import CGVApproval from './cgvapproval';

const fragment = graphql`
  fragment header_userConnected on Query {
    userConnected: viewer {
      ref
      fullUserName
      firstname
      lastname
      email
      logo
      ...avatar_user
      bookmark {
        ...bookmark_book
      }
      host
      rider
      signup {
        checkData
        step1
        step2
        data
      }
      company {
        name
      }
      hasBookings
      hasSpaces
      hasGuestBookings
      corpo {
        ref
        name
        ...layoutCorpoData
      }
      corpoUserConf {
        enable
        status
        isAdmin
        requireValidEmail
      }
      isCGVApproval
    }
  }
`;
const query = graphql`
  query headerQuery {
    ...header_userConnected
  }
`;

const EventComponentScroll = () => {
  const { comefrom } = useZusUserConnectedStore.getState();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [prevScrollPos, setPrevScrollPos] = useState(0);
  const [visible, setVisible] = useState(true);
  const headerRef = useRef(null);
  
  const mapClass = useMemo(() => ({
    search_visible: 'bottom-0',
    search_no_visible: '-bottom-36',
    other_visible: 'bottom-0 translate-y-0',
    other_no_visible: 'bottom-0 translate-y-full',
  }), []);

  const handleScroll = useCallback(() => {
    const currentScrollPos = window.pageYOffset;
    setVisible(prevScrollPos > currentScrollPos || currentScrollPos < 5);
    setPrevScrollPos(currentScrollPos);
  }, [prevScrollPos]);

  useEffect(() => {
    if (!headerRef.current) return;
    
    const classes = 'translate-y-full translate-y-0 -bottom-36'.split(' ');
    classes.forEach(oneClass => headerRef.current.classList.remove(oneClass));
  }, [searchParams, pathname]);

  useEffect(() => {
    let timeoutId;
    const debouncedScroll = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(handleScroll, 10);
    };
    
    window.addEventListener('scroll', debouncedScroll);
    return () => {
      window.removeEventListener('scroll', debouncedScroll);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [handleScroll]);

  useEffect(() => {
    if (!headerRef.current) return;
    
    const classes = 'translate-y-full translate-y-0 bottom-0 -bottom-36'.split(' ');
    classes.forEach(oneClass => headerRef.current.classList.remove(oneClass));
      
    const valueMapClass = mapClass[`${comefrom === 'search' ? 'search' : 'other'}_${visible ? 'visible' : 'no_visible'}`];
    if (valueMapClass) {
      valueMapClass.split(' ').forEach(oneClass => headerRef.current.classList.add(oneClass));
    }
  }, [visible, comefrom, mapClass]);

  return <div ref={headerRef} id="componentHeaderContainer1024" className="fixed bottom-0 z-50 w-full mx-auto transition-all shadow-button from-purple-6/80 to-purple-6/80 bg-gradient-to-b backdrop-blur-lg" />;
};

const HeaderListenerDesktop = ({ closeAllDropDown }) => {
  const pathname = usePathname();

  useEffect(() => {
    closeAllDropDown?.();
  }, [pathname, closeAllDropDown]);

  return null;
};

const generateCorpoLinks = (userConnected, getWasUrl, t) => {
  const links = [
    <a key="headercorpo-activities" className="px-4 py-2 mb-2 text-[15px] rounded-lg lg:mb-0 text-purple-5/80 lg:bg-white bg-purple-0 lg:rounded-none lg:block lg:hover:text-black lg:hover:bg-purple-0" href={getWasUrl('/corpo/activities')} role="menuitem">{t('corpo:layout.activities')}</a>,
  ];

  if (userConnected?.corpoUserConf?.isAdmin) {
    links.push(
      <a key="headercorpo-team" href={getWasUrl('/corpo/team')} className="text-[15px] px-4 py-2 mb-2 rounded-lg lg:mb-0 text-purple-5/80 lg:bg-white bg-purple-0 lg:rounded-none lg:block lg:hover:text-black lg:hover:bg-purple-0" role="menuitem">{t('corpo:layout.team')}</a>,
      <a key="headercorpo-payment" href={getWasUrl('/corpo/payment')} className="text-[15px] px-4 py-2 mb-2 rounded-lg lg:mb-0 text-purple-5/80 lg:bg-white bg-purple-0 lg:rounded-none lg:block lg:hover:text-black lg:hover:bg-purple-0" role="menuitem">{t('corpo:layout.payment')}</a>,
      <a key="headercorpo-params" href={getWasUrl('/corpo/params')} className="text-[15px] px-4 py-2 mb-2 rounded-lg lg:mb-0 text-purple-5/80 lg:bg-white bg-purple-0 lg:rounded-none lg:block lg:hover:text-black lg:hover:bg-purple-0" role="menuitem">{t('corpo:layout.params')}</a>,
      <a key="headercorpo-profile" href={getWasUrl('/corpo/profile')} className="text-[15px] px-4 py-2 mb-2 rounded-lg lg:mb-0 text-purple-5/80 lg:bg-white bg-purple-0 lg:rounded-none lg:block lg:hover:text-black lg:hover:bg-purple-0" role="menuitem">{t('corpo:layout.profile')}</a>,
    );
  }
  
  // links.push(
  //   <a key="headercorpo-help" href={getWasUrl('/corpo/help')} className="text-[15px] px-4 py-2 mb-2 rounded-lg lg:mb-0 text-purple-5/80 lg:bg-white bg-purple-0 lg:rounded-none lg:block lg:hover:text-black lg:hover:bg-purple-0" role="menuitem">{t('corpo:layout.help')}</a>,
  // );
  
  return links;
};
function Header() {
  const reactKey = useMemo(() => 'header-fixed-key', []);
  const langCurrent = lang();
  const { getWasUrl } = useWasUrl({ lang: langCurrent });
  const { t } = useTranslation();
  const dropdown3Ref = useRef();
  const isDesktop = useDeviceDetect(true);

  const {
    open,
    setOpen,
    open2,
    setOpen2,
    open3,
    setOpen3,
    isAuthenticated,
    userConnected,
    headerBackground,
    comefrom,
    disconnect,
    openLogin,
    onClickAddSpace,
    handleMenuToggle
  } = useHeaderState();

  const { headerRef, mobileHeaderRef, visible } = useHeaderScroll();
  const css1024 = useCss1024();

  const headerCorpoLinksD = useMemo(() => 
    generateCorpoLinks(userConnected, getWasUrl, t),
  [userConnected, getWasUrl, t]);

  if (comefrom === 'corpo' && (!isAuthenticated || !userConnected?.corpo?.ref)) {
    return <LoginSignupContainer />;
  }
  
  return (
    <>
      <TrackerComponent />
      <GotoAddSpace />
      <LoginSignupContainer />
      <CorpoUserMessage />
      {!userConnected?.isCGVApproval || <CGVApproval />}
      <nav className={`${['space-public', 'dashboard-host-spaces', 'bookmark', 'dashboard-host-space-edit', 'user-account', 'corpo'].includes(comefrom) 
          ? 'bg-purple-5 border-b border-purple-4/20' 
          : headerBackground}
        transition-bg duration-300 z-20 ${isDesktop ? 'top-0' : 'bottom-0'} w-full
      `}>
        {isDesktop ? (
          <HeaderDesktop
            t={t}
            langCurrent={langCurrent}
            isAuthenticated={isAuthenticated}
            userConnected={userConnected}
            headerBackground={headerBackground}
            comefrom={comefrom}
            disconnect={disconnect}
            openLogin={openLogin}
            onClickAddSpace={onClickAddSpace}
            getWasUrl={getWasUrl}
            dropdown3Ref={dropdown3Ref}
            headerCorpoLinksD={headerCorpoLinksD}
          />
        ) : (
          <HeaderMobile
            t={t}
            isAuthenticated={isAuthenticated}
            userConnected={userConnected}
            open={open}
            open2={open2}
            open3={open3}
            setOpen={setOpen}
            setOpen2={setOpen2}
            setOpen3={setOpen3}
            disconnect={disconnect}
            openLogin={openLogin}
            comefrom={comefrom}
            headerCorpoLinksD={headerCorpoLinksD}
            handleMenuToggle={handleMenuToggle}
          />
        )}
      </nav>
    </>
  );
}

function RefetchContener({ loadQuery, data }) {
  const dataFrag = useFragment(fragment, data);
  const { userConnected } = dataFrag || {};
  const isAuthenticated = useZusUserConnectedStore((state) => state.isAuthenticated);
  const userAuthOpenModal = useZusUserConnectedStore((state) => state.userAuthOpenModal);
  const forceReloadUser = useZusUserConnectedStore((state) => state.forceReloadUser);
  const what = useZusUserConnectedStore((state) => state.what);
  const [userReloaded, setUserReloaded] = useState(false);
  const { comefrom } = useZusUserConnectedStore.getState();

  const handleForceReload = useCallback(() => {
    loadQuery({}, { fetchPolicy: 'network-only' });
    setUserReloaded(nanoid());
  }, [loadQuery]);

  useEffect(() => {
    if (forceReloadUser) {
      handleForceReload();
    }
  }, [forceReloadUser, handleForceReload]);

  useEffect(() => {
    const hasUserData = Boolean(userConnected && Object.keys(userConnected).length);
    
    if (hasUserData && !isAuthenticated) {
      useZusUserConnectedStore.setState({ userData: null });
      handleForceReload();
    } else if (hasUserData) {
      useZusUserConnectedStore.setState({ userData: userConnected });
    } else if (!hasUserData && isAuthenticated) {
      handleForceReload();
    }

    if (what !== 'corpostep' && what !== 'registercompany') {
      if (isAuthenticated && userConnected) {
        const corpoAsking = userConnected?.rider;
        if (corpoAsking) {
          if (!userConnected?.corpo?.ref) {
            userAuthOpenModal('corpostep');
          } else if (what === 'waitcheckcorpo' && userConnected?.corpo?.ref) {
            useZusUserConnectedStore.setState({
              what: 'companyconfirm',
              joinedCorpo: {
                name: userConnected?.corpo?.name,
                teamName: userConnected?.corpo?.teamName || '',
              },
            });
          } else if (what !== 'companyconfirm') {
            userAuthOpenModal('');
          }
        }
      }
    }
  }, [isAuthenticated, userConnected, comefrom, what, userAuthOpenModal, handleForceReload]);

  useEffect(() => {
    if (userReloaded) {
      useZusUserConnectedStore.setState({ checkAtLeastOnce: true });
      const noUserData = !userConnected || Object.keys(userConnected).length === 0;
      if (noUserData && !isAuthenticated) {
        useZusUserConnectedStore.setState({ userData: null, isAuthenticated: false });
        cookie.removeTokenConnection();
      }
    }
  }, [userConnected, isAuthenticated, userReloaded]);

  return null;
}

const MiniQLV3Connection = miniqlv3({
  Component: RefetchContener,
  query,
  fetchPolicy: 'store-only',
});

const ExportComponent = ({ t }) => {
  const headerKey = useMemo(() => nanoid(), []);
  
  return (
    <>
      <MiniQLV3Connection />
      <Header key={headerKey} />
    </>
  );
};

export { MiniQLV3Connection as LoginListener };
export default hocT(ExportComponent);
